import { useTranslation } from 'next-i18next/pages';
import { useStatusMessage } from '@ev-tech/ui-core-components/StatusMessageProvider';
import { Link } from '@ev-tech/ui-core-components/Link';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { alertVariants } from '@ev-tech/ui-core-components/Alert';

import { replaceURLParams } from '@/utils/utmParams';
import { toEmail } from '@/utils/share';
import { MailIcon } from '@/icons/small/Mail';
import { LinkIcon } from '@/icons/small/Link';
import { CrossNaked } from '@/icons/small/CrossNaked';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useExposeTracking } from '@/hooks/useExposeTracking';

import styles from './ShareDialog.module.css';

export const PRESERVED_SHARE_QUERY_PARAMS = ['secretAccessId'];
export const EXPOSE_SHARING_QUERY_PARAM = 'expose_sharing';
export const COPY_LINK_QUERY_PARAM = 'copy_link';
export const EMAIL_QUERY_PARAM = 'email';

type ShareDialogProps = {
  onClose: () => void;
};

export const ShareDialog = ({ onClose }: ShareDialogProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.common);

  const { t: translateExpose } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { t: translateWatchlist } = useTranslation(TRANSLATION_NAMESPACE.watchlist);

  const { showMessage } = useStatusMessage();
  const { trackExposeUserInteractionEvent } = useExposeTracking();

  const copyLink = async () => {
    const urlWithUTMParams = replaceURLParams(
      location.href,
      {
        utm_source: EXPOSE_SHARING_QUERY_PARAM,
        utm_medium: COPY_LINK_QUERY_PARAM,
      },
      PRESERVED_SHARE_QUERY_PARAMS,
    );

    trackExposeUserInteractionEvent({
      elementId: 'share-expose_copy-link',
    });

    try {
      await navigator.clipboard.writeText(urlWithUTMParams);
      showMessage(translateWatchlist('search.watchlist.shareWithLinkCopied.button'), {
        alertType: alertVariants.success,
        duration: 3000,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const draftEmail = () => {
    const urlWithUTMParams = replaceURLParams(
      location.href,
      {
        utm_source: EXPOSE_SHARING_QUERY_PARAM,
        utm_medium: EMAIL_QUERY_PARAM,
      },
      PRESERVED_SHARE_QUERY_PARAMS,
    );

    trackExposeUserInteractionEvent({
      elementId: 'share-expose_email',
    });

    const emailBody = translateExpose('search.expose.share.emailBody', { urlWithUTMParams });

    toEmail(document.title, emailBody);
  };

  return (
    <div className={styles.shareDialog}>
      <Headline className={styles.headline} label={translateExpose('search.expose.share.title')} size="h5" as="p" />
      <Link icon={<LinkIcon />} label={translateWatchlist('search.watchlist.shareWithLink.button')} onClick={copyLink} as="button" />
      <Link icon={<MailIcon />} label={translateExpose('search.common.email')} onClick={draftEmail} as="button" />
      <IconButton
        className={styles.closeBtn}
        onClick={onClose}
        icon={<CrossNaked />}
        variant="tertiary"
        label={t('header.navigation.button.close')}
      />
    </div>
  );
};
